// E8NumberLinePFClosedTask.js
// Zadanie zamkniete E8 P/F: odczytywanie wspolrzednych z osi liczbowej.

import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TruthInput from "../../../components/inputs/TruthInput";
import NumberLineAxis from "../../../components/diagrams/NumberLineAxis";

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function buildData({
  firstKnownIndex,
  firstKnownValue,
  secondKnownIndex,
  secondKnownValue,
  bIndex,
  bThreshold,
}) {
  const step = (secondKnownValue - firstKnownValue) / (secondKnownIndex - firstKnownIndex);
  const aValue = firstKnownValue - firstKnownIndex * step;
  const bValue = aValue + bIndex * step;
  const cValue = aValue + 6 * step;

  return {
    firstKnownIndex,
    firstKnownValue,
    secondKnownIndex,
    secondKnownValue,
    bIndex,
    bThreshold,
    step,
    aValue,
    bValue,
    cValue,
    correctPF: [cValue % 2 === 0 ? "P" : "F", bValue < bThreshold ? "P" : "F"],
  };
}

function createOriginalTask() {
  return {
    type: "E8NumberLinePFClosedTask",
    data: buildData({
      firstKnownIndex: 1,
      firstKnownValue: 56,
      secondKnownIndex: 4,
      secondKnownValue: 83,
      bIndex: 2.5,
      bThreshold: 74,
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 12,
    },
    tags: ["e8", "liczby_calkowite", "os_liczbowa", "prawda_falsz"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const step = pick([4, 5, 6, 7, 8, 9]);
  const firstKnownIndex = 1;
  const secondKnownIndex = 4;
  const bIndex = pick([1.5, 2.5, 3.5, 4.5, 5.5]);
  const aValue = pick([18, 24, 31, 37, 42, 49]);
  const firstKnownValue = aValue + firstKnownIndex * step;
  const secondKnownValue = aValue + secondKnownIndex * step;
  const bValue = aValue + bIndex * step;
  const leftNeighborIndex = Math.floor(bIndex);
  const rightNeighborIndex = Math.ceil(bIndex);
  const labeledIndexes = new Set([firstKnownIndex, secondKnownIndex]);
  const thresholdCandidates = [leftNeighborIndex, rightNeighborIndex].filter(
    (index) => !labeledIndexes.has(index)
  );
  const bThresholdIndex = pick(
    thresholdCandidates.length > 0
      ? thresholdCandidates
      : [leftNeighborIndex, rightNeighborIndex]
  );
  const bThreshold = aValue + bThresholdIndex * step;

  return {
    type: "E8NumberLinePFClosedTask",
    data: buildData({
      firstKnownIndex,
      firstKnownValue,
      secondKnownIndex,
      secondKnownValue,
      bIndex,
      bThreshold,
    }),
    source: { variant: "random", basedOn: "E8_2025_MAJ_12" },
    tags: ["e8", "liczby_calkowite", "os_liczbowa", "prawda_falsz"],
  };
}

function NumberLineDiagram({ data }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "8px 0 16px" }}>
      <NumberLineAxis
        divisions={6}
        topLabels={[{ index: 0, math: "A" }, { index: data.bIndex, math: "B" }, { index: 6, math: "C" }]}
        markers={[{ index: 0 }, { index: data.bIndex }, { index: 6 }]}
        bottomLabels={[
          { index: data.firstKnownIndex, math: `${data.firstKnownValue}` },
          { index: data.secondKnownIndex, math: `${data.secondKnownValue}` },
        ]}
        ariaLabel="Oś liczbowa z punktami A, B i C"
      />
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Na osi liczbowej zaznaczono punkty <InlineMath math="A" />,{" "}
        <InlineMath math="B" /> i <InlineMath math="C" />. Odcinek{" "}
        <InlineMath math="AC" /> jest podzielony na <InlineMath math="6" /> równych
        części.
      </p>
      <NumberLineDiagram data={data} />
      <p>
        Oceń prawdziwość podanych zdań. Wybierz <InlineMath math="P" />, jeśli
        zdanie jest prawdziwe, albo <InlineMath math="F" />, jeśli jest fałszywe.
      </p>
      <p>
        1. Współrzędna punktu <InlineMath math="C" /> jest liczbą parzystą.
      </p>
      <p>
        2. Współrzędna punktu <InlineMath math="B" /> jest liczbą mniejszą od{" "}
        <InlineMath math={`${data.bThreshold}`} />.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const setValue = (key, nextValue) =>
    onChange?.({ ...value, [key]: nextValue });

  return (
    <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
      {[["s1", "1."], ["s2", "2."]].map(([key, label]) => (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ minWidth: 18 }}>{label}</span>
          <TruthInput
            value={value[key] || null}
            disabled={disabled}
            onChange={(nextValue) => setValue(key, nextValue)}
          />
        </div>
      ))}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return (
    userAnswer?.s1 === data.correctPF[0] &&
    userAnswer?.s2 === data.correctPF[1]
  );
}

export function renderAnswer({ data }) {
  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>Poprawna odpowiedź:</p>
      <p>
        1. <InlineMath math={`\\text{${data.correctPF[0]}}`} />
      </p>
      <p>
        2. <InlineMath math={`\\text{${data.correctPF[1]}}`} />
      </p>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer?.s1 && !userAnswer?.s2) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>Twoja odpowiedź:</p>
      <p>
        1. <InlineMath math={userAnswer?.s1 ? `\\text{${userAnswer.s1}}` : "-"} />
      </p>
      <p>
        2. <InlineMath math={userAnswer?.s2 ? `\\text{${userAnswer.s2}}` : "-"} />
      </p>
    </div>
  );
}

export function renderExplanation({ data }) {
  const knownDistance = data.secondKnownValue - data.firstKnownValue;
  const indexDistance = data.secondKnownIndex - data.firstKnownIndex;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Najpierw odczytujemy, ile równych odcinków jest
        między podanymi liczbami na osi.
      </p>
      <BlockMath
        math={`${data.secondKnownValue}-${data.firstKnownValue}=${knownDistance}`}
      />
      <p>
        Między tymi kreskami są <InlineMath math={`${indexDistance}`} /> równe
        odcinki, więc jeden odcinek ma długość:
      </p>
      <BlockMath
        math={`\\frac{${knownDistance}}{${indexDistance}}=${data.step}`}
      />
      <p>
        <strong>Krok 2.</strong> Wyznaczamy współrzędne punktów. Od znanej liczby
        cofamy się do punktu <InlineMath math="A" />, a potem idziemy kolejnymi
        równymi odcinkami do punktów <InlineMath math="B" /> i{" "}
        <InlineMath math="C" />.
      </p>
      <BlockMath
        math={`A=${data.firstKnownValue}-${data.firstKnownIndex}\\cdot${data.step}=${data.aValue}`}
      />
      <BlockMath
        math={`B=${data.aValue}+${data.bIndex}\\cdot${data.step}=${data.bValue}`}
      />
      <BlockMath
        math={`C=${data.aValue}+6\\cdot${data.step}=${data.cValue}`}
      />
      <p>
        <strong>Krok 3.</strong> Sprawdzamy pierwsze zdanie. Liczba{" "}
        <InlineMath math={`${data.cValue}`} /> jest{" "}
        <strong>{data.cValue % 2 === 0 ? "parzysta" : "nieparzysta"}</strong>,
        więc pierwsze zdanie jest{" "}
        <strong>{data.correctPF[0] === "P" ? "prawdziwe" : "fałszywe"}</strong>.
      </p>
      <p>
        <strong>Krok 4.</strong> Sprawdzamy drugie zdanie.
      </p>
      <BlockMath
        math={`${data.bValue}${data.bValue < data.bThreshold ? "<" : "\\nless"}${data.bThreshold}`}
      />
      <p>
        Drugie zdanie jest{" "}
        <strong>{data.correctPF[1] === "P" ? "prawdziwe" : "fałszywe"}</strong>.
      </p>
      <p>
        <strong>Krok 5.</strong> Zaznaczamy odpowiedzi{" "}
        <InlineMath
          math={`1.\\ \\text{${data.correctPF[0]}}\\quad\\text{i}\\quad 2.\\ \\text{${data.correctPF[1]}}`}
        />
        .
      </p>
    </div>
  );
}
